/* eslint-disable max-len */
import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DispatchFunc } from "../store/ActionTypes";
import { clearFilters as clearFiltersAction, updateFilters } from "../store/FilterActions";
import { FilterScope, getFilters, hasSearchableFilters } from "../store/FilterTypes";
import { ReduxState } from "../store/ReduxState";
import { remoteClearError, remoteClearResponse, remoteTrigger } from "../store/RemoteActions";
import { RemoteScope } from "../store/RemoteTypes";
import { isValidLicensePlate, formatLicensePlate, parseLicenseplate } from "@vwpfs/mary-air-lib/utils/LicensePlate";
import { FieldTypes } from "@vwpfs/mary-air-lib/components/air/07-organisms/Form/Field";
import { hasDateValueValidation, hasLicensePlateValidation, hasValueValidation, ValidationFeedback } from "@vwpfs/mary-air-lib/components/air/07-organisms/Form/Field.utils";

/**
 * Supported field types for generic filters
 */
// export type GenericFieldType = "text" | "licensePlate" | "select" | "number" | "date";

/**
 * Validation function type - uses the existing ValidationFeedback from Field.utils
 */
export type ValidationFunction = (value: string | number) => ValidationFeedback;

/**
 * Option for select fields
 */
export interface SelectOption {
    label: string;
    value: string | number;
}

/**
 * Complete field configuration combining all field information
 */
export interface FieldConfig {
    /** Human-readable label for the field */
    label: string;
    /** Field type determining the input component */
    type: FieldTypes;
    /** Options for select fields */
    options?: SelectOption[];
    /** Placeholder text for select fields */
    placeholder?: string;
    /** Custom validation function */
    validation?: ValidationFunction;
    /** Whether to preserve original order of options (don't sort alphabetically) */
    unsorted?: boolean;
}

/**
 * Generic field configuration
 */
export interface GenericFieldConfig<TFilters> {
    key: keyof TFilters;
    label: string;
    type: FieldTypes;
    placeholder?: string;
    options?: SelectOption[];
    validationFunction?: ValidationFunction;
    /** Whether to preserve original order of options (don't sort alphabetically) */
    unsorted?: boolean;
}

/**
 * Filter configuration for a specific filter type
 */
export interface FilterConfig<TFilters> {
    filterScope: FilterScope;
    remoteScope: RemoteScope;
    fields: Array<GenericFieldConfig<TFilters>>;
}

/**
 * Filter metadata interface - contains all the static configuration for a filter type
 */
export interface FilterMetadata<TFilters> {
    labels: Record<keyof TFilters, string>;
    fieldTypes: Record<keyof TFilters, FieldTypes>;
    keys: Array<keyof TFilters>;
    selectOptions?: Partial<Record<keyof TFilters, {
        options: SelectOption[];
        placeholder?: string;
    }>>;
}

/**
 * Dynamic filter config generator - creates a FilterConfig from metadata and scopes
 */
export const createFilterConfig = <TFilters>(
    filterScope: FilterScope,
    remoteScope: RemoteScope,
    metadata: FilterMetadata<TFilters>,
): FilterConfig<TFilters> => ({
    filterScope,
    remoteScope,
    fields: metadata.keys.map(key => {
        const baseConfig: GenericFieldConfig<TFilters> = {
            key,
            label: metadata.labels[key],
            type: metadata.fieldTypes[key],
        };

        // Add select-specific configuration if available
        const selectConfig = metadata.selectOptions?.[key];
        if (selectConfig && metadata.fieldTypes[key] === "select") {
            return {
                ...baseConfig,
                placeholder: selectConfig.placeholder,
                options: selectConfig.options,
            };
        }

        return baseConfig;
    }),
});

/**
 * Simplified configuration options for creating a filter config
 * Everything in one place - no more separate labels, types, keys!
 */
export interface FilterConfigOptions<TFilters> {
    filterScope: FilterScope;
    remoteScope: RemoteScope;
    /** All field configurations in one object */
    fields: Record<keyof TFilters, FieldConfig>;
}

/**
 * Ultra-simple filter config generator - everything in one fields object!
 * No more separate labels, fieldTypes, keys, and fieldSpecificConfig
 */
export const createSimpleFilterConfig = <TFilters>(
    options: FilterConfigOptions<TFilters>,
): FilterConfig<TFilters> => {
    const keys = Object.keys(options.fields) as Array<keyof TFilters>;

    return {
        filterScope: options.filterScope,
        remoteScope: options.remoteScope,
        fields: keys.map(key => {
            const field = options.fields[key];
            return {
                key,
                label: field.label,
                type: field.type,
                placeholder: field.placeholder,
                options: field.options,
                validationFunction: field.validation,
                unsorted: field.unsorted,
            };
        }),
    };
};

/**
 * Generic field renderer utility - can be used in any filter component
 * This eliminates the need to write renderField functions in each component
 */
export const createGenericFieldRenderer = <TFilters>(
    config: FilterConfig<TFilters>,
    fieldHelpers: {
        createChangeHandler: (key: keyof TFilters) => (value?: string | number) => void;
        getFieldValue: (key: keyof TFilters) => string | number;
        getFieldDisabled: (key: keyof TFilters) => boolean;
    },
    defaultFilters?: Partial<TFilters>,
) => (fieldKey: keyof TFilters) => {
    const fieldConfig = config.fields.find(field => field.key === fieldKey);
    if (!fieldConfig) {
        return null;
    }

    // Don't render fields that have default values (they are contextual filters)
    if (defaultFilters?.[fieldKey] !== undefined) {
        return null;
    }

    const baseProps = {
        label: fieldConfig.label,
        onChange: fieldHelpers.createChangeHandler(fieldConfig.key),
        value: fieldHelpers.getFieldValue(fieldConfig.key),
        disabled: fieldHelpers.getFieldDisabled(fieldConfig.key),
        readOnly: fieldHelpers.getFieldDisabled(fieldConfig.key),
        validationFunction: createFieldValidation(fieldConfig),
    };

    // Add type-specific props automatically from config
    const typeSpecificProps = fieldConfig.type === FieldTypes.LicensePlate ? { type: FieldTypes.LicensePlate } :
        fieldConfig.type === FieldTypes.Date ? { type: FieldTypes.Date } :
        fieldConfig.type === FieldTypes.Select ? {
            type: FieldTypes.Select,
            placeholder: fieldConfig.placeholder,
            options: fieldConfig.options,
            unsorted: fieldConfig.unsorted,
        } : { type: fieldConfig.type };

    return { ...baseProps, ...typeSpecificProps };
};

/**
 * Convenience hook that returns both the filter functionality and field renderer
 * This combines useGenericFilters with the field renderer for maximum convenience
 */
export const useGenericFiltersWithRenderer = <TFilters extends Record<string, unknown>>(
    config: FilterConfig<TFilters>,
    defaultFilters?: Partial<TFilters>,
    loadingResults = false,
) => {
    const filterHooks = useGenericFilters(config, defaultFilters, loadingResults);

    const getFieldProps = createGenericFieldRenderer(config, {
        createChangeHandler: filterHooks.createChangeHandler,
        getFieldValue: filterHooks.getFieldValue,
        getFieldDisabled: filterHooks.getFieldDisabled,
    }, defaultFilters);

    return {
        ...filterHooks,
        getFieldProps,
    };
};

/**
 * Props for components using generic filters
 */
export interface GenericFilterProps<TFilters> {
    loadingResults: boolean;
    /** Permanent filters from route/context (e.g., account ID from URL path) */
    defaultFilters?: Partial<TFilters>;
}

/**
 * Generic field value processor - handles special formatting and validation per field type
 */
export const processFieldValue = <TFilters>(
    fieldConfig: GenericFieldConfig<TFilters>,
    value?: string | number,
): string | number | undefined => {
    if (!value) {
        return undefined;
    }

    switch (fieldConfig.type) {
        case FieldTypes.Number:
            return Number(value);
        case FieldTypes.LicensePlate: {
            const stringValue = value.toString();
            return isValidLicensePlate(stringValue)
                ? parseLicenseplate(stringValue) ?? stringValue.toUpperCase()
                : stringValue.toUpperCase();
        }
        case FieldTypes.Text:
        case FieldTypes.Select:
        default:
            return value.toString();
    }
};

/**
 * Generic field value getter - handles special display formatting per field type
 */
export const getFieldDisplayValue = <TFilters>(
    fieldConfig: GenericFieldConfig<TFilters>,
    value: unknown,
    defaultValue: unknown,
): string | number => {
    // We check if defaultValue is an empty string and treat it as undefined
    const fieldValue = (defaultValue !== "" ? defaultValue : undefined) ?? value;
    if (!fieldValue) {
        return "";
    }

    switch (fieldConfig.type) {
        case FieldTypes.LicensePlate: {
            if (typeof fieldValue === "string") {
                return isValidLicensePlate(fieldValue)
                    ? formatLicensePlate(fieldValue)
                    : fieldValue.toUpperCase();
            }
            return fieldValue as string | number;
        }
        case FieldTypes.Number:
        case FieldTypes.Text:
        case FieldTypes.Select:
        default:
            return fieldValue as string | number;
    }
};

/**
 * Create validation function based on field type and configuration
 */
export const createFieldValidation = <TFilters>(
    fieldConfig: GenericFieldConfig<TFilters>,
): ValidationFunction => {
    // Use custom validation if provided
    if (fieldConfig.validationFunction) {
        return fieldConfig.validationFunction;
    }

    // Default validation based on field type
    switch (fieldConfig.type) {
        case FieldTypes.LicensePlate:
            return (value: string | number) =>
                hasLicensePlateValidation(value, `${fieldConfig.label} provided`);
        case FieldTypes.Date:
            return (value: string | number) =>
                hasDateValueValidation(value.toString(), `${fieldConfig.label} provided`);
        case FieldTypes.Text:
        case FieldTypes.Select:
        case FieldTypes.Number:
        default:
            return (value: string | number) =>
                hasValueValidation(value.toString(), `${fieldConfig.label} provided`);
    }
};

/**
 * Generic filter hook that provides all filter-related functionality
 *
 * @param config Filter configuration defining fields and scopes
 * @param defaultFilters Permanent filters from route/context (e.g., account ID from URL path).
 *                      These filters are automatically applied, not editable by users, and
 *                      come from route parameters or other contextual data.
 * @param loadingResults Whether results are currently loading
 */
export const useGenericFilters = <TFilters extends Record<string, unknown>>(
    config: FilterConfig<TFilters>,
    defaultFilters?: Partial<TFilters>,
    loadingResults = false,
) => {
    const dispatch = useDispatch<DispatchFunc>();

    // Stabilize config to prevent unnecessary re-renders
    const configRef = useRef(config);
    configRef.current = config;

    const searchEnabled = useSelector((state: ReduxState) =>
        hasSearchableFilters(state, configRef.current.filterScope, defaultFilters),
    );

    const filters = useSelector((state: ReduxState) =>
        getFilters(state, configRef.current.filterScope),
    ) as Partial<TFilters>;

    // Generic field configuration getters
    const getFieldConfig = useCallback((fieldKey: keyof TFilters): GenericFieldConfig<TFilters> | undefined =>
        configRef.current.fields.find(field => field.key === fieldKey), []);

    // Generic value getter with defaultFilters fallback and special formatting
    const getFieldValue = useCallback((fieldKey: keyof TFilters): string | number => {
        const fieldConfig = getFieldConfig(fieldKey);
        if (!fieldConfig) {
            return "";
        }

        const defaultValue = defaultFilters?.[fieldKey];
        const filterValue = filters?.[fieldKey];

        return getFieldDisplayValue(fieldConfig, filterValue, defaultValue);
    }, [defaultFilters, filters, getFieldConfig]);

    // Generic function to check if field should be disabled/readonly based on defaultFilters
    const getFieldDisabled = useCallback((fieldKey: keyof TFilters): boolean =>
        !!(defaultFilters?.[fieldKey]), [defaultFilters]);

    // Helper function to update filters
    const onChange = useCallback(<K extends keyof TFilters>(key: K, value: TFilters[K]) => {
        dispatch(updateFilters({ [key]: value }, configRef.current.filterScope));
    }, [dispatch]);

    // Generic change handler for all fields
    const createChangeHandler = useCallback((fieldKey: keyof TFilters) =>
        (value?: string | number) => {
            const fieldConfig = getFieldConfig(fieldKey);
            if (!fieldConfig) {
                return;
            }

            const processedValue = processFieldValue(fieldConfig, value);
            onChange(fieldKey, processedValue as TFilters[keyof TFilters]);
        }, [onChange, getFieldConfig]);

    // Action handlers
    const loadResults = useCallback(() => {
        dispatch(remoteTrigger(configRef.current.remoteScope, { skip: 0 }));
    }, [dispatch]);

    const clearResults = useCallback(() => {
        dispatch(remoteClearResponse(configRef.current.remoteScope));
        dispatch(remoteClearError(configRef.current.remoteScope));
    }, [dispatch]);

    // Custom clearFilters function that handles the typing issue
    const clearFilters = useCallback(() => {
        const filterKeys = Object.keys(filters ?? {});
        // We need to cast this properly since the clearFiltersAction has strict typing
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        dispatch(clearFiltersAction(filterKeys as any, configRef.current.filterScope));
    }, [dispatch, filters]);

    const initFilters = useCallback(() => {
        if (defaultFilters) {
            dispatch(updateFilters(defaultFilters, configRef.current.filterScope));
        }
        dispatch(remoteTrigger(configRef.current.remoteScope, { skip: 0 }));
    }, [defaultFilters, dispatch]);

    const initAndClearFilters = useCallback(() => {
        clearFilters();
        initFilters();
    }, [clearFilters, initFilters]);

    const onSearchFilter = useCallback(() => {
        loadResults();
    }, [loadResults]);

    return {
        // State
        searchEnabled,
        filters,
        loadingResults,

        // Field helpers
        getFieldConfig,
        getFieldValue,
        getFieldDisabled,
        createChangeHandler,

        // Actions
        loadResults,
        clearResults,
        clearFilters,
        initFilters,
        initAndClearFilters,
        onSearchFilter,
    };
};
